import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Volume2, VolumeX } from 'lucide-react';
import { useVideoSound } from '../../../hooks/useVideoSound';

interface VideoComparisonProps {
  beforeVideo: string;
  afterVideo: string;
}

export const VideoComparison: React.FC<VideoComparisonProps> = ({ beforeVideo, afterVideo }) => {
  const { isMuted, toggleSound } = useVideoSound();
  const beforeVideoRef = useRef<HTMLVideoElement>(null);
  const afterVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (beforeVideoRef.current) {
      beforeVideoRef.current.muted = isMuted;
    }
    if (afterVideoRef.current) {
      afterVideoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-16 relative">
      {/* Original Video */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative rounded-xl overflow-hidden aspect-video bg-gray-100"
      >
        <video
          ref={beforeVideoRef}
          src={beforeVideo}
          className="w-full h-full object-cover"
          autoPlay
          muted={isMuted}
          loop
          playsInline
        />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-4">
          <p className="text-white text-sm font-medium">Original Video</p>
        </div>
      </motion.div>

      {/* Arrow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block z-10">
        <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
          <ArrowRight className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Transformed Video */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="relative rounded-xl overflow-hidden aspect-video bg-gray-100"
      >
        <video
          ref={afterVideoRef}
          src={afterVideo}
          className="w-full h-full object-cover"
          autoPlay
          muted={isMuted}
          loop
          playsInline
        />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-4">
          <p className="text-white text-sm font-medium">Magical Transformation</p>
        </div>
      </motion.div>

      {/* Sound Toggle Button */}
      <button
        onClick={toggleSound}
        className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 transition-colors p-2 rounded-full text-white"
        aria-label={isMuted ? "Unmute video" : "Mute video"}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5" />
        ) : (
          <Volume2 className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};